/** GENERATED_ASSET — tier walls, chipped slate spillway slabs and the spout stele. */
import type { Ctx } from './ctx';
import type { Layer } from './svgKit';
import { Path } from './pathKit';
import type { Pt } from './pathKit';
import { SLAB_D, SLAB_T } from './layout';
import type { TierLayout } from './layout';
import { slabFrontLine } from './wear';

function paintWall(ctx: Ctx, L: Layer, t: TierLayout): void {
  const { sw, style } = ctx;
  const top = t.edgeBottom, h = t.base - top;
  L.poly([{ x: t.x0, y: top }, { x: t.x1, y: top }, { x: t.x1, y: t.base }, { x: t.x0, y: t.base }], { fill: sw.stone.base, stroke: sw.outline, sw: style.outerStroke, part: `tier${t.k}-wall` });
  // Right-hand shade for volume, then courses.
  L.poly([{ x: t.x1 - 16, y: top + 1 }, { x: t.x1 - 1, y: top + 1 }, { x: t.x1 - 1, y: t.base - 1 }, { x: t.x1 - 16, y: t.base - 1 }], { fill: sw.stone.shadow, fo: 0.3, part: 'wall-shade' });
  const r = ctx.rng(`tier.blocks.${t.k}`);
  const rows = h >= 34 ? 3 : 2;
  const rowH = h / rows;
  for (let row = 0; row < rows; row++) {
    const y0 = top + row * rowH, y1 = y0 + rowH;
    let x = t.x0 + (row % 2 ? 14 : 4);
    while (x < t.x1 - 12) {
      const w = r.range(24, 40), xe = Math.min(t.x1 - 1, x + w), tone = r.next();
      if (xe - x > 10 && tone < 0.3) L.poly([{ x, y: y0 }, { x: xe, y: y0 }, { x: xe, y: y1 }, { x, y: y1 }], { fill: sw.stone.light, fo: 0.3, part: 'block-lit' });
      else if (xe - x > 10 && tone > 0.8) L.poly([{ x, y: y0 }, { x: xe, y: y0 }, { x: xe, y: y1 }, { x, y: y1 }], { fill: sw.stone.shadow, fo: 0.25, part: 'block-dim' });
      if (xe < t.x1 - 8) L.line(xe, y0, xe, y1, { stroke: sw.stone.seam, sw: style.seamStroke, so: 0.7, part: 'joint' });
      x = xe;
    }
    if (row > 0) L.line(t.x0 + 1.5, y0, t.x1 - 1.5, y0, { stroke: sw.stone.seam, sw: style.seamStroke, so: 0.7, part: 'course' });
  }
  // Slab overhang shadow.
  L.poly([{ x: t.x0 + 1, y: top }, { x: t.x1 - 1, y: top }, { x: t.x1 - 1, y: top + 5 }, { x: t.x0 + 1, y: top + 5 }], { fill: sw.outline, fo: 0.22, part: 'overhang-shadow' });
  if (t.k === 0) {
    L.poly([{ x: t.x0 + 1, y: t.base - 6 }, { x: t.x1 - 1, y: t.base - 6 }, { x: t.x1 - 1, y: t.base - 1 }, { x: t.x0 + 1, y: t.base - 1 }], { fill: sw.stone.deep, fo: 0.3, part: 'waterline-wet' });
  }
}

function paintSlab(ctx: Ctx, L: Layer, t: TierLayout): void {
  const { sw, style } = ctx;
  const front = slabFrontLine(ctx, t.k);
  const bottomY = t.s + SLAB_T;
  const jag = ctx.rng(`slab.bottom.${t.k}`);
  // Edge face (the thick slate edge) first, then the top surface above it.
  const edge: Pt[] = [...front, { x: t.slabX1, y: bottomY }, { x: t.slabX1 - 18, y: bottomY + jag.range(-0.5, 0.5) }, { x: t.slabX0 + 22, y: bottomY + jag.range(-0.5, 0.5) }, { x: t.slabX0, y: bottomY }];
  L.poly(edge, { fill: sw.slate.edge, stroke: sw.outline, sw: style.outerStroke, part: `tier${t.k}-slab-edge` });
  // Cleavage lines (broken, not full-width) read as layered slate.
  const cr = ctx.rng(`slab.cleave.${t.k}`);
  for (const dy of [3.4, 6.3]) {
    let x = t.slabX0 + 6;
    while (x < t.slabX1 - 14) {
      const len = cr.range(14, 34);
      const xe = Math.min(t.slabX1 - 6, x + len);
      if (!(xe > t.lipX - t.notchW / 2 - 2 && x < t.lipX + t.notchW / 2 + 2)) L.line(x, t.s + dy, xe, t.s + dy, { stroke: sw.slate.seam, sw: 0.9, so: 0.6, part: 'cleavage' });
      x = xe + cr.range(6, 16);
    }
  }
  const top: Pt[] = [...front, { x: t.slabX1 - t.slabBack, y: t.s - SLAB_D }, { x: t.slabX0 + t.slabBack, y: t.s - SLAB_D }];
  L.poly(top, { fill: sw.slate.top, stroke: sw.outline, sw: style.seamStroke + 0.3, part: `tier${t.k}-slab-top` });
  // Sheen strokes along the slab surface plus back-edge shade where the next tier stands.
  const sr = ctx.rng(`slab.sheen.${t.k}`);
  for (let i = 0; i < 3; i++) {
    const x = sr.range(t.slabX0 + 14, t.slabX1 - 40), y = t.s - SLAB_D + 3 + i * 3 + sr.range(-0.6, 0.6);
    L.line(x, y, x + sr.range(14, 30), y, { stroke: sw.slate.sheen, sw: 1.1, so: 0.55, part: 'sheen' });
  }
  if (t.k < ctx.L.T - 1) {
    const up = ctx.L.tiers[t.k + 1]!;
    L.poly([{ x: up.x0 - 2, y: t.s - SLAB_D + 0.8 }, { x: up.x1 + 2, y: t.s - SLAB_D + 0.8 }, { x: up.x1 + 2, y: t.s - SLAB_D + 4.6 }, { x: up.x0 - 2, y: t.s - SLAB_D + 4.6 }], { fill: sw.outline, fo: 0.2, part: 'upper-tier-shadow' });
  }
}

export function paintTiers(ctx: Ctx, L: Layer): void {
  for (let k = ctx.L.T - 1; k >= 0; k--) {
    const t = ctx.L.tiers[k]!;
    paintWall(ctx, L, t);
    paintSlab(ctx, L, t);
  }
}

/** The back stele that carries the brass spout. dry_ruin has a snapped top and a fallen chunk. */
export function paintStele(ctx: Ctx, L: Layer): void {
  const { sw, style, L: lay } = ctx;
  const s = lay.stele;
  const top = lay.tiers[lay.T - 1]!;
  const broken = ctx.v.brokenStele;
  const bt = s.bodyTop;
  const topPts: Pt[] = broken
    ? [{ x: s.x1, y: bt + 9 }, { x: s.x1 - 7, y: bt + 3 }, { x: s.x1 - 17, y: bt + 10 }, { x: s.x1 - 27, y: bt + 2 }, { x: s.x1 - 36, y: bt + 12 }, { x: s.x0 + 14, y: bt + 6 }, { x: s.x0 + 6, y: bt + 14 }, { x: s.x0, y: bt + 21 }]
    : [{ x: s.x1, y: bt }, { x: s.x0, y: bt }];
  L.poly([{ x: s.x0, y: s.baseY }, ...topPts.slice().reverse(), { x: s.x1, y: s.baseY }].reverse(), { fill: sw.stone.base, stroke: sw.outline, sw: style.outerStroke, part: 'stele-body' });
  L.poly([{ x: s.x1 - 14, y: bt + 14 }, { x: s.x1 - 1, y: bt + 12 }, { x: s.x1 - 1, y: s.baseY - 1 }, { x: s.x1 - 14, y: s.baseY - 1 }], { fill: sw.stone.shadow, fo: 0.32, part: 'stele-shade' });
  L.line(s.x0 + 2.5, bt + 18, s.x0 + 2.5, s.baseY - 3, { stroke: sw.stone.lit, sw: 1.4, so: 0.65, part: 'stele-light' });
  // Carved shell niche around the spout plate, then two tide grooves.
  const nx = (s.x0 + s.x1) / 2;
  L.path(new Path().M(nx - 17, s.plateY + 14).L(nx - 17, s.plateY - 2).Q(nx - 17, s.plateY - 15, nx, s.plateY - 15).Q(nx + 17, s.plateY - 15, nx + 17, s.plateY - 2).L(nx + 17, s.plateY + 14).Z(),
    { fill: sw.stone.shadow, stroke: sw.stone.seam, sw: 1.3, part: 'stele-niche' });
  for (const [i, dy] of [18, 29].entries()) {
    const y = s.baseY - dy, amp = 2.4;
    const p = new Path().M(s.x0 + 6, y);
    for (let x = s.x0 + 6, n = 0; x < s.x1 - 8; x += 11, n++) p.Q(x + 5.5, y + (n % 2 ? amp : -amp) * (i ? -1 : 1), Math.min(x + 11, s.x1 - 6), y);
    L.path(p, { stroke: sw.stone.seam, sw: 1.6, so: 0.7, part: 'stele-groove' });
  }
  // Cap (whole stele only): slate slab with a visible top face.
  if (!broken) {
    L.poly([{ x: s.capX0, y: s.capFront }, { x: s.capX1, y: s.capFront }, { x: s.capX1, y: bt }, { x: s.capX0, y: bt }], { fill: sw.slate.edge, stroke: sw.outline, sw: style.outerStroke, part: 'stele-cap-edge' });
    L.poly([{ x: s.capX0 + 4, y: s.capTop }, { x: s.capX1 - 4, y: s.capTop }, { x: s.capX1, y: s.capFront }, { x: s.capX0, y: s.capFront }], { fill: sw.slate.top, stroke: sw.outline, sw: style.seamStroke + 0.3, part: 'stele-cap-top' });
    L.line(s.capX0 + 14, s.capTop + 3.5, s.capX0 + 30, s.capTop + 3.5, { stroke: sw.slate.sheen, sw: 1.1, so: 0.55, part: 'sheen' });
    L.poly([{ x: s.x0 + 1, y: bt }, { x: s.x1 - 1, y: bt }, { x: s.x1 - 1, y: bt + 4 }, { x: s.x0 + 1, y: bt + 4 }], { fill: sw.outline, fo: 0.2, part: 'cap-shadow' });
  } else {
    // Fallen chunks on the top slab, in front of the stele base.
    const y = top.s - 2.5;
    const cx = top.slabX0 + 26;
    L.poly([{ x: cx - 11, y }, { x: cx - 9, y: y - 8 }, { x: cx + 2, y: y - 9 }, { x: cx + 7, y: y - 2 }, { x: cx + 4, y }], { fill: sw.stone.base, stroke: sw.outline, sw: style.seamStroke + 0.3, part: 'fallen-chunk' });
    L.poly([{ x: cx - 9, y: y - 8 }, { x: cx + 2, y: y - 9 }, { x: cx + 1, y: y - 5 }, { x: cx - 8, y: y - 4 }], { fill: sw.stone.light, fo: 0.55, part: 'fallen-chunk-top' });
    L.poly([{ x: cx + 12, y }, { x: cx + 13, y: y - 4.5 }, { x: cx + 19, y: y - 5 }, { x: cx + 20, y }], { fill: sw.stone.shadow, stroke: sw.outline, sw: style.seamStroke, part: 'fallen-chunk-small' });
  }
  L.poly([{ x: s.x0 - 3, y: s.baseY }, { x: s.x1 + 3, y: s.baseY }, { x: s.x1 + 3, y: s.baseY + 1.6 }, { x: s.x0 - 3, y: s.baseY + 1.6 }], { fill: sw.outline, fo: 0.25, part: 'stele-foot-shadow' });
}
